'use client';

import { useQuery } from '@tanstack/react-query';
import { format, isSameDay } from 'date-fns';
import { de, enUS, pt } from 'date-fns/locale';
import { ChevronLeft, Loader2, Package } from 'lucide-react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { JSX, useMemo } from 'react';

import { PageHeader, PageHeaderTitle } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getQueryConfig } from '@/lib/tanstackQuery/queryRegistry';
import { cn } from '@/lib/utils';
import { parseDateOnly } from '@/lib/utils/date-only';

import { OrderCard } from './order-card';

interface OrderDatePageProps {
  date: string;
}

const DATE_LOCALES = {
  de,
  en: enUS,
  pt,
} as const;

function formatPageDate(date: string, locale: string): string {
  const dateLocale = DATE_LOCALES[locale as keyof typeof DATE_LOCALES] ?? enUS;
  return format(parseDateOnly(date), 'EEEE, d MMMM yyyy', { locale: dateLocale });
}

export function OrderDatePage({ date }: OrderDatePageProps): JSX.Element {
  const t = useTranslations('employeeOrders');
  const locale = useLocale();
  const { data, isLoading, error } = useQuery({
    ...getQueryConfig('employee-orders-history'),
  });

  const matchingOrders = useMemo(() => {
    const day = parseDateOnly(date);
    return (data?.orders ?? [])
      .filter((order) => isSameDay(order.meal_date, day))
      .sort((a, b) => a.daypart.localeCompare(b.daypart));
  }, [data?.orders, date]);

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 max-xs:space-y-4">
      <PageHeader className="max-xs:gap-2">
        <div className="space-y-3">
          <Button variant="ghost" size="sm" asChild className="-ml-2 w-fit">
            <Link href="/app/orders">
              <ChevronLeft className="h-4 w-4" />
              {t('detail.backToOrders')}
            </Link>
          </Button>
          <PageHeaderTitle className="max-xs:text-xl">
            {formatPageDate(date, locale)}
          </PageHeaderTitle>
        </div>
      </PageHeader>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>{t('error')}</p>
        </div>
      ) : matchingOrders.length === 0 ? (
        <EmptyDateState />
      ) : (
        <div className="space-y-3">
          {matchingOrders.map((order) => (
            <OrderCard key={order.order_slot_id} order={order} blockNavigation={true} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyDateState() {
  const t = useTranslations('employeeOrders');

  return (
    <Card>
      <CardContent className="pb-12 pt-16 text-center text-muted-foreground max-xs:pb-8 max-xs:pt-10">
        <span
          className={cn(
            'inline-flex items-center justify-center',
            'mx-auto mb-4 h-16 w-16 rounded-2xl max-xs:h-12 max-xs:w-12',
            'bg-muted/50',
            'transition-colors duration-200',
          )}
        >
          <Package className="h-8 w-8 text-muted-foreground/70 max-xs:h-6 max-xs:w-6" />
        </span>
        <p className="font-medium text-foreground">{t('detail.emptyTitle')}</p>
        <p className="mx-auto mt-1.5 max-w-[260px] text-sm text-muted-foreground/70 max-xs:text-xs">
          {t('detail.emptyDescription')}
        </p>
      </CardContent>
    </Card>
  );
}
